/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the Errors component that is the entry point into the react Errors.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Typings
 */
import { useParams } from 'react-router';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI Container component.
 */
import { Container } from '@material-ui/core';

/**
 * Material UI Paper component.
 */
import Paper from '@material-ui/core/Paper';

/**
 * Material UI Typography component.
 */
import Typography from '@material-ui/core/Typography';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IErrorsRouteParams {
    errorCode: string;
}

interface IErrorsProps {

}

interface IErrorType {
    code: string;
    title: string;
    message: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    page: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    container: {
        flexGrow: 1
    },
    paper: {
        padding: theme.spacing(3, 2),
        margin: theme.spacing(3, 0)
    },
    heading: {
        paddingBottom: theme.spacing(1)
    },
    '@global': {
        '#root': {
            height: '100%'
        },
        body: {
            height: '100%'
        },
        html: {
            height: '100%'
        }
    }
}));

const errorTypes = {
    403: { code: '403', title: 'Forbidden', message: 'The current user has insufficient permissions to access the requested resource.' } as IErrorType,
    404: { code: '404', title: 'Page Not Found', message: 'The requested resource could not be found. Please check the requested URL is correct.' } as IErrorType,
    generic: { code: '500', title: 'Internal Server Error', message: 'An error occurred on the server while attempting to process the request, please try again.' } as IErrorType
}

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const Errors: React.FunctionComponent<IErrorsProps> = () => {
    const classes = useStyles();

    const params = useParams<IErrorsRouteParams>();

    let errorType = errorTypes.generic;

    if (params && params.errorCode && errorTypes[params.errorCode]) {
        errorType = errorTypes[params.errorCode];
    }

    return (
        <>
            <div
                className={classes.page}
            >
                <Container
                    className={classes.container}
                >
                    <Paper
                        className={classes.paper}
                    >
                        <Typography
                            variant="h1"
                            color="secondary"
                            className={classes.heading}
                        >
                            {errorType.code}
                        </Typography>
                        <Typography
                            variant="h2"
                            color="primary"
                            className={classes.heading}
                        >
                            {errorType.title}
                        </Typography>
                        <Typography
                            variant="body1"
                        >
                            {errorType.message}
                        </Typography>
                    </Paper>
                </Container>
            </div>
        </>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default Errors;